import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { nameIdNonRequiredSchema, nameIdSchemaRequired } from '~constants/regexConst';
import { AccountBasic } from '~hooks/useAccount/models';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { SiteBasic } from '~hooks/useSites/models';

// A blank price is "not priced", which the API models as null rather than a real
// 0 — nil and empty input both have to leave the schema undefined for the payload
// helper below to tell them apart from a typed 0.
const optionalPriceSchema = yup
  .number()
  .transform((_, val) =>
    isNil(val) || String(val).trim() === '' ? undefined : Number(val),
  )
  .min(0)
  .typeError('Numbers only.')
  .notRequired();

export const materialRatePriceToPayload = (price?: number | null) =>
  isNil(price) || String(price).trim() === '' ? null : Number(price);

export const materialRateSchema = yup.object().shape({
  customer: nameIdNonRequiredSchema.notRequired(),
  project: nameIdNonRequiredSchema.notRequired(),
  site: nameIdNonRequiredSchema.notRequired(),
  material: nameIdSchemaRequired.required(),
  unitOfMeasure: nameIdSchemaRequired.required(),
  buyPrice: optionalPriceSchema,
  sellPrice: optionalPriceSchema,
});

export type MaterialRateDTO = yup.InferType<typeof materialRateSchema>;

export const setMaterialRatesSchemaDefaultValues = (
  materialRate?: MaterialRate,
  customer?: Pick<AccountBasic, 'id' | 'name'>,
  site?: Pick<SiteBasic, 'id' | 'name'>,
) => {
  const defaultValues: MaterialRateDTO = {
    customer: {
      id: materialRate?.customerAccount?.id ?? customer?.id ?? '',
      name: materialRate?.customerAccount?.name ?? customer?.name ?? '',
    },
    project: {
      id: materialRate?.project?.id ?? '',
      name: materialRate?.project?.name ?? '',
    },
    site: {
      id: materialRate?.site?.id ?? site?.id ?? '',
      name: materialRate?.site?.name ?? site?.name ?? '',
    },
    material: {
      id: materialRate?.material.id ?? '',
      name: materialRate?.material.name ?? '',
    },
    unitOfMeasure: {
      id: materialRate?.unitOfMeasure ?? '',
      name: materialRate?.unitOfMeasure ?? '',
    },
    buyPrice: isNil(materialRate?.buyPrice)
      ? ('' as unknown as number)
      : (materialRate?.buyPrice.toFixed(2) as unknown as number),
    sellPrice: isNil(materialRate?.sellPrice)
      ? ('' as unknown as number)
      : (materialRate?.sellPrice.toFixed(2) as unknown as number),
  };

  return defaultValues;
};
